import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { FaUserDoctor } from "react-icons/fa6";
import { toast } from "react-hot-toast";
import upload from "../images/blogs/upload.svg";
import { Trash2, Upload as UploadIcon, ImagePlus, X } from "lucide-react";
import { api_url } from "../config/config";
import { FaCalendarAlt, FaIdCard } from "react-icons/fa";
import { GiMedicines } from "react-icons/gi";
import { TiTickOutline } from "react-icons/ti";
import { Helmet } from "react-helmet";
import { RxCross2 } from "react-icons/rx";
import { IoIosInformationCircleOutline } from "react-icons/io";
import { getFileKind } from "../utils/fileType";

const plusPattern = `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`;

const STATUS_COLOR = {
  Prescription_Uploaded: "bg-blue-50 text-blue-700 border-blue-200",
  InReview: "bg-amber-50 text-amber-700 border-amber-200",
  Approved: "bg-[#eaf4f3] text-[#1e4a46] border-[#d4ecea]",
  AddedToCart: "bg-[#eaf4f3] text-[#1e4a46] border-[#d4ecea]",
  Rejected: "bg-red-50 text-red-600 border-red-200",
  Pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  Cancelled: "bg-red-50 text-red-500 border-red-200",
};

const MyPrescription = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [showValidModal, setShowValidModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [prescriptionFile, setPrescriptionFile] = useState(null);
  const [prescriptionNotes, setPrescriptionNotes] = useState("");
  const [previousPrescriptions, setPreviousPrescriptions] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [errorStates, setErrorStates] = useState({});
  const [visiblePrescriptions, setVisiblePrescriptions] = useState(6);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [prescriptionToDelete, setPrescriptionToDelete] = useState(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  // Matches the limit used in Cartpage.js's prescription upload, so the
  // same file isn't accepted on one page and rejected on the other.
  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const fetchPrescriptions = async () => {
    const authToken = localStorage.getItem("authToken");
    if (!authToken) return;
    try {
      setLoading(true);
      const res = await axios.get(`${api_url}/user-prescription`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setPreviousPrescriptions(res.data.data || []);
    } catch {
      toast.error("Failed to fetch prescriptions");
    } finally {
      setLoading(false);
    }
  };

  const handleImageError = (id) =>
    setErrorStates((prev) => ({ ...prev, [id]: true }));

  const handleUploadClick = () => {
    if (!localStorage.getItem("authToken")) {
      toast.error("Please log in before uploading a prescription.");
      return;
    }
    setShowModal(true);
  };

  const handleRemoveImage = (index) => {
    const updated = [...uploadedImages];
    updated.splice(index, 1);
    setUploadedImages(updated);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (file) {
      if (!allowedTypes.includes(file.type)) {
        toast.error("Only JPG, PNG, PDF, DOC or DOCX files allowed");
        event.target.value = "";
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error("File must be under 5MB");
        event.target.value = "";
        return;
      }
      setPrescriptionFile(file);
    }
  };

  const handlePrescriptionUploadClick = async () => {
    if (isUploading) return;
    const authToken = localStorage.getItem("authToken");
    setIsUploading(true);
    if (!authToken) {
      toast.error("Please log in.");
      setIsUploading(false);
      return;
    }

    if (editingIndex !== null) {
      try {
        const formData = new FormData();
        if (prescriptionFile) formData.append("file", prescriptionFile);
        formData.append("description_notes", prescriptionNotes);
        setLoading(true);
        const response = await axios.patch(
          `${api_url}/user-prescription/${editingIndex}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${authToken}`,
            },
          },
        );
        setPreviousPrescriptions((prev) =>
          prev.map((p) =>
            p.prescription_id === editingIndex ? response.data.data : p,
          ),
        );
        toast.success("Prescription updated successfully.");
        setShowModal(false);
        setPrescriptionNotes("");
        setPrescriptionFile(null);
        setEditingIndex(null);
        fetchPrescriptions();
      } catch {
        toast.error("Something went wrong.");
      } finally {
        setIsUploading(false);
        setLoading(false);
      }
      return;
    }

    try {
      if (!prescriptionFile) {
        toast.error("No prescription file selected.");
        return;
      }
      const formData = new FormData();
      formData.append("prescription_file", prescriptionFile);
      formData.append("description_notes", prescriptionNotes);
      uploadedImages.forEach((f) => formData.append("additional_images", f));
      setLoading(true);
      const response = await axios.post(`${api_url}/prescription`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${authToken}`,
        },
      });
      setPreviousPrescriptions((prev) => [...prev, response.data.data]);
      toast.success(
        "Prescription uploaded successfully. Our team will reach you soon.",
      );
      setShowModal(false);
      setPrescriptionNotes("");
      setPrescriptionFile(null);
      setUploadedImages([]);
      fetchPrescriptions();
    } catch {
      toast.error("Failed to upload prescription.");
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!prescriptionToDelete) return;
    try {
      await axios.delete(`${api_url}/prescription/${prescriptionToDelete}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      setPreviousPrescriptions((prev) =>
        prev.filter((p) => p.prescription_id !== prescriptionToDelete),
      );
      toast.success("Prescription deleted successfully.");
    } catch {
      toast.error("Failed to delete prescription.");
    } finally {
      setShowDeleteModal(false);
      setPrescriptionToDelete(null);
    }
  };

  const handleImageUpload = (e) => {
    // Same constraints as Cartpage.js's "Medicine Images" upload — max 5
    // images, 5MB each — so a set of files valid there isn't silently
    // capped differently or let through oversized here.
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    const files = Array.from(e.target.files);
    const validFiles = [];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: only JPG, JPEG, or PNG images are allowed.`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: File must be under 5MB`);
        continue;
      }
      validFiles.push(file);
    }
    e.target.value = "";
    setUploadedImages((prev) => [...prev, ...validFiles].slice(0, 5));
  };

  return (
    <>
      <Helmet>
        <title>My Prescriptions | MediSYS Pharmacy</title>
        <meta
          name="description"
          content="Upload and manage your prescriptions at MediSYS Pharmacy."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-[#f8fafa]  mt-36 lg:mt-0 font-poppins">
        {/* ── Hero strip ── */}
        <div className="relative bg-[#162c2a] overflow-hidden px-5 sm:px-8 lg:px-14 pt-8 pb-12">
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: plusPattern,
              backgroundRepeat: "repeat",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="absolute -top-10 right-0 w-72 h-72 rounded-full bg-[#5fbdb3]/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="w-5 h-[2px] bg-[#ff601e] rounded-full" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5fbdb3]">
                  Account
                </span>
              </div>
              <h1 className="font-bold text-[24px] sm:text-[30px] text-white">
                My Prescriptions
              </h1>
              <p className="text-[13px] text-white/50 mt-1">
                Upload your prescription and we'll deliver your medicines
              </p>
            </div>

            <div className="hidden sm:flex items-center gap-3">
              <button
                onClick={() => setShowValidModal(true)}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-[#5fbdb3] hover:text-white transition-colors"
              >
                <IoIosInformationCircleOutline size={16} />
                What's a valid prescription?
              </button>
              <button
                onClick={handleUploadClick}
                className=" flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[12px] px-4 h-9 rounded-xl transition-all duration-200 shadow-[0_2px_8px_rgba(255,96,30,0.3)]"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload Prescription
              </button>
            </div>
            <div className="sm:hidden flex flex-col xs:flex-row sm:flex-row items-start xs:items-center gap-2 sm:gap-3">
              <button
                onClick={() => setShowValidModal(true)}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-[#5fbdb3] hover:text-white transition-colors"
              >
                <IoIosInformationCircleOutline size={16} />
                What's a valid prescription?
              </button>
              <button
                onClick={handleUploadClick}
                className="flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[12px] px-4 h-9 rounded-xl transition-all duration-200 shadow-[0_2px_8px_rgba(255,96,30,0.3)] w-full xs:w-auto"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                Upload Prescription
              </button>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-8 lg:px-14 py-8 -mt-4">
          <div className="flex items-center gap-4 mb-6">
            <span className="w-5 h-[2px] bg-[#ff601e] rounded-full" />
            <h2 className="font-bold text-[17px] sm:text-[18px] text-[#1e4a46]">
              Uploaded Prescriptions
            </h2>
            <div className="flex-1 h-px bg-[#e2eaea]" />
            {!loading && (
              <span className="text-[13px] font-medium text-[#8faba9] bg-[#eaf4f3] border border-[#d4ecea] rounded-full px-3 py-1">
                {previousPrescriptions.length} prescription
                {previousPrescriptions.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl border border-[#e2eaea] p-5 animate-pulse"
                >
                  <div className="flex gap-4 mb-4">
                    <div className="w-16 h-16 bg-[#e2eaea] rounded-xl flex-shrink-0" />
                    <div className="flex-1 flex flex-col gap-2">
                      <div className="h-4 bg-[#e2eaea] rounded-full w-20" />
                      <div className="h-3 bg-[#e2eaea] rounded-full w-full" />
                      <div className="h-3 bg-[#e2eaea] rounded-full w-2/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : previousPrescriptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#5fbdb3"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <p className="font-semibold text-[16px] text-[#374747]">
                No prescriptions uploaded yet
              </p>
              <p className="text-[14px] text-[#8faba9]">
                Upload a prescription to get started
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {previousPrescriptions
                .slice(0, visiblePrescriptions)
                .map((prescription, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.07)] overflow-hidden hover:shadow-[0_4px_20px_rgba(22,44,42,0.12)] transition-shadow duration-200"
                  >
                    <div className="h-[2.5px] bg-gradient-to-r from-[#162c2a] via-[#5fbdb3] to-[#ff601e]" />
                    <div className="p-4">
                      {/* Top: thumbnail + status */}
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-14 h-14 rounded-xl border border-[#e2eaea] bg-[#f8fafa] flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {getFileKind(prescription.image_url).isImage &&
                          !errorStates[prescription.id] ? (
                            <a
                              href={prescription.image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full h-full"
                            >
                              <img
                                src={prescription.image_url}
                                alt="prescription"
                                onError={() =>
                                  handleImageError(prescription.id)
                                }
                                className="w-full h-full object-cover"
                              />
                            </a>
                          ) : (
                            <a
                              href={prescription.image_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full h-full flex flex-col items-center justify-center gap-0.5 hover:bg-[#eaf4f3] transition-colors"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#5fbdb3"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                              </svg>
                              <span className="text-[9px] font-bold text-[#1e4a46]">
                                {getFileKind(prescription.image_url).label}
                              </span>
                            </a>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            {prescription.status && (
                              <span
                                className={`text-[12px] font-bold px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUS_COLOR[prescription.status] || "bg-gray-100 text-gray-600 border-gray-200"}`}
                              >
                                {prescription.status}
                              </span>
                            )}
                          </div>
                          {prescription.status === "Rejected" &&
                            prescription.rejection_reason && (
                              <p className="text-[13px] text-red-500 mt-1.5 leading-snug">
                                {prescription.rejection_reason}
                              </p>
                            )}
                          {prescription.status === "AddedToCart" &&
                            prescription.updated_at && (
                              <p className="text-[12px] text-[#5fbdb3] mt-1">
                                Added to cart:{" "}
                                {new Date(
                                  prescription.updated_at,
                                ).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })}
                              </p>
                            )}
                        </div>
                      </div>

                      <div className="h-px bg-[#f1f5f5] mb-3" />

                      {/* Notes */}
                      {prescription.description_notes && (
                        <p className="text-[13px] text-[#607d7b] leading-relaxed mb-2 line-clamp-2">
                          <span className="font-semibold text-[#374747]">
                            Notes:{" "}
                          </span>
                          {prescription.description_notes}
                        </p>
                      )}

                      {/* Uploaded at */}
                      <div className="flex items-center gap-1.5 text-[12px] text-[#8faba9]">
                        <svg
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <polyline points="12 6 12 12 16 14" />
                        </svg>
                        {prescription.created_at
                          ? new Date(prescription.created_at).toLocaleString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )
                          : "N/A"}
                      </div>

                      {/* Additional images */}
                      {prescription.PrescriptionImage?.length > 0 && (
                        <div className="mt-3">
                          <p className="text-[13px] font-semibold text-[#374747] mb-2">
                            Medicine Images
                          </p>
                          <div className="grid grid-cols-4 gap-1.5">
                            {prescription.PrescriptionImage.slice(0, 8).map(
                              (imgObj, imgIndex) => (
                                <img
                                  key={imgObj.image_id || imgIndex}
                                  src={imgObj.image_url}
                                  alt={`medicine-${imgIndex}`}
                                  className="w-full h-14 object-cover rounded-lg border border-[#e2eaea]"
                                />
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {previousPrescriptions.length > visiblePrescriptions && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setVisiblePrescriptions((prev) => prev + 6)}
                className="flex items-center gap-2 h-10 px-6 rounded-xl bg-[#162c2a] hover:bg-[#1e4a46] text-white text-[13px] font-semibold transition-all duration-200 shadow-[0_2px_8px_rgba(22,44,42,0.25)]"
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
                View More
              </button>
            </div>
          )}
        </div>

        {/* ── Valid prescription modal ── */}
        {showValidModal && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0a1715]/60 backdrop-blur-sm px-4">
            {" "}
            <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] w-full max-w-[560px] overflow-hidden">
              <div className="relative bg-[#162c2a] px-6 py-5 flex items-center justify-between">
                <div
                  className="absolute inset-0 opacity-[0.05] pointer-events-none"
                  style={{
                    backgroundImage: plusPattern,
                    backgroundRepeat: "repeat",
                    backgroundSize: "40px 40px",
                  }}
                />
                <h2 className="relative font-bold text-[16px] text-white">
                  What makes a valid prescription?
                </h2>
                <button
                  onClick={() => setShowValidModal(false)}
                  className="relative w-8 h-8 rounded-xl bg-white/[0.1] hover:bg-white/[0.2] flex items-center justify-center transition-colors"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {[
                    {
                      icon: <FaUserDoctor size={22} />,
                      label: "Doctor Details",
                    },
                    {
                      icon: <FaCalendarAlt size={22} />,
                      label: "Prescription Date",
                    },
                    { icon: <FaIdCard size={22} />, label: "Patient Details" },
                    {
                      icon: <GiMedicines size={22} />,
                      label: "Dosage Details",
                    },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex flex-col items-center gap-2 text-center"
                    >
                      <div className="w-12 h-12 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center text-[#1e4a46]">
                        {item.icon}
                      </div>
                      <p className="text-[11px] font-semibold text-[#374747]">
                        {item.label}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="h-px bg-[#f1f5f5] mb-4" />
                <p className="text-[11px] font-semibold text-[#8faba9] uppercase tracking-wide mb-3">
                  Do's and Don'ts
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-3 bg-[#eaf4f3] border border-[#d4ecea] rounded-xl p-3">
                    <TiTickOutline
                      className="text-[#1e4a46] flex-shrink-0 mt-0.5"
                      size={18}
                    />
                    <div>
                      <p className="text-[13px] font-semibold text-[#1e4a46]">
                        Upload a Clear Image
                      </p>
                      <p className="text-[11px] text-[#607d7b] mt-0.5">
                        Place on a flat surface, ensure handwriting/type is
                        clearly visible.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-[#feecec] border border-[#fcd5d5] rounded-xl px-3 py-2.5">
                    <RxCross2
                      className="text-red-500 flex-shrink-0"
                      size={16}
                    />
                    <p className="text-[13px] font-semibold text-red-600">
                      No Photos of Medicine Packaging
                    </p>
                  </div>
                  <div className="flex items-center gap-3 bg-[#feecec] border border-[#fcd5d5] rounded-xl px-3 py-2.5">
                    <RxCross2
                      className="text-red-500 flex-shrink-0"
                      size={16}
                    />
                    <p className="text-[13px] font-semibold text-red-600">
                      Do Not Crop the Image
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Upload modal ── */}
        {showModal && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-[#0a1715]/60 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] w-full max-w-[520px] max-h-[90vh] overflow-hidden flex flex-col">
              <div className="relative bg-[#162c2a] px-6 py-5 flex items-center justify-between flex-shrink-0">
                <div
                  className="absolute inset-0 opacity-[0.05] pointer-events-none"
                  style={{
                    backgroundImage: plusPattern,
                    backgroundRepeat: "repeat",
                    backgroundSize: "40px 40px",
                  }}
                />
                <div className="relative">
                  <h2 className="font-bold text-[16px] text-white">
                    {editingIndex !== null
                      ? "Edit Prescription"
                      : "Upload Prescription"}
                  </h2>
                  <p className="text-[11px] text-white/50 mt-0.5">
                    JPG, PNG, PDF, DOC or DOCX · Max 5MB per file
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingIndex(null);
                    setPrescriptionNotes("");
                  }}
                  className="relative w-8 h-8 rounded-xl bg-white/[0.1] hover:bg-white/[0.2] flex items-center justify-center transition-colors"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="overflow-y-auto scrollbar-hide p-6 flex flex-col gap-5">
                {editingIndex !== null && (
                  <div className="flex items-center gap-2 bg-[#fff3ee] border border-[#ffd5c2] rounded-xl px-3 py-2.5">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ff601e"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <p className="text-[12px] font-medium text-[#ff601e]">
                      Upload a new file to replace the existing prescription.
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-[11px] font-bold text-[#1e3330] uppercase tracking-wide mb-2">
                    Prescription File
                  </p>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-2xl p-5 text-center cursor-pointer transition-all duration-200
                      ${prescriptionFile ? "border-[#5fbdb3] bg-[#e8f5f4]" : "border-[#c8d8d7] hover:border-[#5fbdb3] hover:bg-[#e8f5f4]"}`}
                  >
                    {prescriptionFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-10 h-10 bg-[#ddf0ee] border border-[#aac9c7] rounded-xl flex items-center justify-center flex-shrink-0">
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#5fbdb3"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <p className="text-[13px] font-bold text-[#1e4a46]">
                            {prescriptionFile.name}
                          </p>
                          <p className="text-[11px] text-[#2d5250]">
                            {(prescriptionFile.size / 1024 / 1024).toFixed(2)}{" "}
                            MB · Click to change
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="w-12 h-12 bg-[#ddf0ee] border border-[#aac9c7] rounded-2xl flex items-center justify-center mx-auto mb-3">
                          <UploadIcon size={20} className="text-[#5fbdb3]" />
                        </div>
                        <p className="text-[14px] font-bold text-[#1e4a46] mb-1">
                          Click to upload your prescription
                        </p>
                        <p className="text-[12px] text-[#2d5250]">
                          or drag and drop here
                        </p>
                        <p className="text-[11px] text-[#2d5250] mt-1.5">
                          JPG, PNG, PDF, DOC or DOCX · Maximum 5MB
                        </p>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <p className="text-[11px] text-[#5fbdb3] flex items-center gap-1 mt-2">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#5fbdb3"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="10" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    Your prescription is stored securely and reviewed only by
                    our licensed pharmacists.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-[11px] font-bold text-[#1e3330] uppercase tracking-wide">
                    Notes / Description{" "}
                    <span className="text-[#8faba9] font-normal normal-case">
                      (optional)
                    </span>
                  </label>
                  <textarea
                    className="w-full border-[1.5px] border-[#e2eaea] rounded-xl px-4 py-3 text-[13px] text-[#374747] placeholder-[#8faba9] outline-none focus:border-[#1e4a46] focus:shadow-[0_0_0_3px_rgba(95,189,179,0.15)] transition-all resize-none"
                    rows={3}
                    placeholder="Any notes for our pharmacist…"
                    maxLength={500}
                    onChange={(e) => setPrescriptionNotes(e.target.value)}
                    value={prescriptionNotes}
                  />
                  <p className="text-[10px] text-[#8faba9] text-right">
                    {prescriptionNotes.length}/500
                  </p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[11px] font-bold text-[#1e3330] uppercase tracking-wide">
                      Medicine Images{" "}
                      <span className="text-[#8faba9] font-normal normal-case">
                        (optional · max 5)
                      </span>
                    </label>
                    {/* {editingIndex === null &&
                      uploadedImages.length > 0 &&
                      uploadedImages.length < 5 && (
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          className="flex items-center gap-1 text-[11px] font-semibold text-[#5fbdb3] hover:text-[#1e4a46] transition-colors"
                        >
                          <ImagePlus size={13} />
                          Add Photos
                        </button>
                      )} */}
                  </div>

                  {uploadedImages.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {uploadedImages.map((image, i) => (
                        <div
                          key={i}
                          className="relative w-16 h-16 rounded-xl overflow-hidden border-[1.5px] border-[#aac9c7] bg-[#e8f5f4] flex-shrink-0"
                        >
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`med-${i}`}
                            className="w-full h-full object-cover"
                          />
                          {editingIndex === null && (
                            <button
                              onClick={() => handleRemoveImage(i)}
                              className="absolute top-0.5 right-0.5 w-4.5 h-4.5 bg-red-500 rounded-full flex items-center justify-center border-none cursor-pointer"
                              style={{ width: 16, height: 16 }}
                            >
                              <X size={9} color="white" />
                            </button>
                          )}
                        </div>
                      ))}
                      {editingIndex === null && uploadedImages.length < 5 && (
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          className="w-16 h-16 rounded-xl border-2 border-dashed border-[#c8d8d7] hover:border-[#5fbdb3] bg-[#f2f8f7] hover:bg-[#e8f5f4] flex flex-col items-center justify-center transition-all cursor-pointer"
                        >
                          <ImagePlus size={16} className="text-[#aac9c7]" />
                          <span className="text-[9px] text-[#aac9c7] mt-0.5">
                            Add
                          </span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() =>
                        editingIndex === null &&
                        imageInputRef.current?.click()
                      }
                      className={`border-2 border-dashed rounded-xl px-4 py-3 flex items-center gap-3 transition-all ${editingIndex !== null ? "border-[#e2eaea] cursor-not-allowed opacity-60" : "border-[#c8d8d7] hover:border-[#5fbdb3] cursor-pointer hover:bg-[#e8f5f4]"}`}
                    >
                      <div className="w-9 h-9 bg-[#e8f5f4] border border-[#aac9c7] rounded-xl flex items-center justify-center flex-shrink-0">
                        <ImagePlus size={16} className="text-[#5fbdb3]" />
                      </div>
                      <div>
                        <p className="text-[12px] font-semibold text-[#1e4a46]">
                          Add medicine photos
                        </p>
                        <p className="text-[10px] text-[#2d5250]">
                          JPG or PNG · up to 5 images · 5MB each
                        </p>
                      </div>
                    </div>
                  )}

                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png"
                    ref={imageInputRef}
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#f1f5f5] flex-shrink-0 bg-[#f8fafa]">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingIndex(null);
                    setPrescriptionNotes("");
                  }}
                  className="h-10 px-5 rounded-xl border border-[#e2eaea] text-[#374747] text-[13px] font-semibold hover:bg-[#e2eaea] transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePrescriptionUploadClick}
                  // A file is required to add a new prescription, but not
                  // to edit one (the customer may just be changing notes) —
                  // only require it when there's no existing prescription
                  // being edited, same rule Cartpage.js's upload uses.
                  disabled={
                    isUploading ||
                    (editingIndex === null && !prescriptionFile)
                  }
                  className="h-10 px-5 rounded-xl bg-[#162c2a] hover:bg-[#1e4a46] text-white text-[13px] font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-[0_2px_8px_rgba(22,44,42,0.25)]"
                >
                  {isUploading ? (
                    <>
                      <svg
                        className="animate-spin w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="white"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="white"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Uploading…
                    </>
                  ) : (
                    <>
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Add Prescription
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Delete confirm modal ── */}
        {showDeleteModal && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-[#0a1715]/60 backdrop-blur-sm px-4">
            <div className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.25)] w-full max-w-[400px] p-6 flex flex-col items-center gap-4 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[#feecec] border border-[#fcd5d5] flex items-center justify-center">
                <Trash2 className="text-red-500" size={24} />
              </div>
              <div>
                <h3 className="font-bold text-[16px] text-[#1e4a46] mb-1">
                  Delete Prescription?
                </h3>
                <p className="text-[13px] text-[#607d7b]">
                  This action cannot be undone. The prescription will be
                  permanently removed.
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 h-10 rounded-xl border border-[#e2eaea] text-[#374747] text-[13px] font-semibold hover:bg-[#f1f5f5] transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="flex-1 h-10 rounded-xl bg-red-500 hover:bg-red-600 text-white text-[13px] font-semibold transition-all shadow-[0_2px_8px_rgba(239,68,68,0.3)]"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MyPrescription;
